import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, Eye, EyeOff, Sparkles, Calendar } from 'lucide-react';
import { toast } from 'sonner';

// Founder launch date
const FOUNDER_LAUNCH_DATE = new Date('2026-07-31T09:00:00+02:00');

// Countdown hook
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Countdown for Founders
  const timeLeft = useCountdown(FOUNDER_LAUNCH_DATE);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    setMaintenanceMode(false);
    
    try {
      await login(email, password);
      toast.success('¡Bienvenido de nuevo!');
      navigate('/dashboard');
    } catch (error) {
      // Check for maintenance mode (403 with specific detail)
      if (error.response?.status === 403 && error.response?.data?.detail === 'maintenance_mode') {
        setMaintenanceMode(true);
        toast.info('Tu acceso se activará el 31 de julio de 2026');
      } else {
        toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="orb orb-brand w-[600px] h-[600px] -top-[200px] -left-[200px] animate-float-slow"
          style={{ opacity: 0.4 }}
        />
        <div 
          className="orb orb-lavender w-[500px] h-[500px] -bottom-[150px] -right-[150px] animate-float-slower"
          style={{ opacity: 0.3, animationDelay: '2s' }}
        />
        <div 
          className="orb orb-periwinkle w-[400px] h-[400px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-float-slow"
          style={{ opacity: 0.2, animationDelay: '4s' }}
        />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/logo-dark.png" 
              alt="FASCINAI ONE" 
              className="h-14 object-contain"
            />
          </div>
          <p className="text-white/60 text-lg">Inicia sesión en tu cuenta</p>
        </div>

        {/* Founder Access Countdown Alert */}
        {maintenanceMode && (
          <div 
            className="glass-dark rounded-2xl p-6 mb-6 border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-yellow-500/5"
            data-testid="founder-access-alert"
          >
            <div className="text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500/30 to-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="text-white font-bold text-xl mb-2">👋 ¡Bienvenido, Founder!</h3>
              <p className="text-white/70 text-sm mb-4">
                Tu acceso se activará el <strong className="text-amber-300">31 de julio de 2026</strong>
              </p>
              
              {/* Countdown */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="bg-black/30 rounded-lg p-2">
                  <div className="text-xl font-bold text-white">{timeLeft.days}</div>
                  <div className="text-[10px] text-white/50">días</div>
                </div>
                <div className="bg-black/30 rounded-lg p-2">
                  <div className="text-xl font-bold text-white">{timeLeft.hours}</div>
                  <div className="text-[10px] text-white/50">horas</div>
                </div>
                <div className="bg-black/30 rounded-lg p-2">
                  <div className="text-xl font-bold text-white">{timeLeft.minutes}</div>
                  <div className="text-[10px] text-white/50">min</div>
                </div>
                <div className="bg-black/30 rounded-lg p-2">
                  <div className="text-xl font-bold text-white">{timeLeft.seconds}</div>
                  <div className="text-[10px] text-white/50">seg</div>
                </div>
              </div>
              
              <p className="text-white/40 text-xs mt-3">
                Te avisaremos por email cuando lancemos
              </p>
            </div>
          </div>
        )}

        {/* Form Card - Glass Effect */}
        <div className="glass-dark rounded-3xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70 text-sm font-medium">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 bg-white/5 border-white/10 focus:border-brand-500 text-white placeholder:text-white/30 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-brand-500/20"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70 text-sm font-medium">
                Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 bg-white/5 border-white/10 focus:border-brand-500 text-white placeholder:text-white/30 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-brand-500/20"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-glow-brand disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Iniciando sesión...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Iniciar Sesión
                </div>
              )}
            </Button>
            
            {/* Forgot Password Link */}
            <div className="text-center mt-4">
              <Link 
                to="/forgot-password" 
                className="text-white/50 hover:text-brand text-sm transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/50">
              ¿No tienes cuenta?{' '}
              <Link 
                to="/registro" 
                className="text-lime-400 hover:text-lime-300 font-medium transition-colors"
              >
                Regístrate
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/30 text-sm mt-8">
          © 2024 FASCINAI ONE. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default Login;
